import { type Hex, keccak256 } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';

import { createIndexedDbStore } from './indexedDBHelper';

const DB_NAME = 'leverup-1ct-wallet';
const DB_VERSION = 1;
const STORE_NAME = 'oneClickWallets';

const AES_ALGO_NAME = 'AES-GCM';
const AES_KEY_LENGTH = 256;
const AES_IV_LENGTH = 12;

export type OneClickWalletRecord = {
  owner: string;
  oneClickWalletAddress: string;
  cryptoKey: CryptoKey;
  encryptedPk: ArrayBuffer;
  iv: ArrayBuffer;
  createdAt: number;
};

let oneClickWalletStore:
  | ReturnType<typeof createIndexedDbStore<OneClickWalletRecord>>
  | undefined;

function getOneClickWalletStore() {
  if (!oneClickWalletStore) {
    oneClickWalletStore = createIndexedDbStore<OneClickWalletRecord>({
      dbName: DB_NAME,
      version: DB_VERSION,
      storeName: STORE_NAME,
      keyPath: 'owner',
    });
  }

  return oneClickWalletStore;
}

async function generateAesGcmKey(): Promise<CryptoKey> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Web Crypto is not available');
  }

  return window.crypto.subtle.generateKey(
    {
      name: AES_ALGO_NAME,
      length: AES_KEY_LENGTH,
    },
    false, // extractable: false
    ['encrypt', 'decrypt'],
  );
}

function generateIv(): ArrayBuffer {
  if (typeof window === 'undefined' || !window.crypto) {
    throw new Error('Web Crypto is not available');
  }

  const ivArray = new Uint8Array(AES_IV_LENGTH);
  window.crypto.getRandomValues(ivArray);
  
return ivArray.buffer;
}

async function encryptPrivateKeyWithKey(
  key: CryptoKey,
  privateKey: string,
  iv: ArrayBuffer,
): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(privateKey);
  const data = encoded.buffer;

  const algo: any = {
    name: AES_ALGO_NAME,
    iv,
  };
  
return window.crypto.subtle.encrypt(algo, key as any, data as any);
}

async function decryptPrivateKeyWithKey(
  key: CryptoKey,
  encryptedPk: ArrayBuffer,
  iv: ArrayBuffer,
): Promise<string> {
  const algo: any = {
    name: AES_ALGO_NAME,
    iv,
  };
  const decrypted = await window.crypto.subtle.decrypt(
    algo,
    key as any,
    encryptedPk as any,
  );

  const decoder = new TextDecoder();
  
return decoder.decode(decrypted);
}

export async function createEncryptedOneClickWalletRecord(params: {
  owner: string;
  oneClickWalletAddress: string;
  privateKey: string;
}): Promise<OneClickWalletRecord> {
  const key = await generateAesGcmKey();
  const iv = generateIv();
  const encryptedPk = await encryptPrivateKeyWithKey(
    key,
    params.privateKey,
    iv,
  );

  return {
    owner: params.owner,
    oneClickWalletAddress: params.oneClickWalletAddress,
    cryptoKey: key,
    encryptedPk,
    iv,
    createdAt: Date.now(),
  };
}

export async function decryptPrivateKeyFromRecord(
  record: OneClickWalletRecord,
): Promise<string> {
  return decryptPrivateKeyWithKey(
    record.cryptoKey,
    record.encryptedPk,
    record.iv,
  );
}

export async function putOneClickWalletRecord(
  record: OneClickWalletRecord,
): Promise<void> {
  try {
    await getOneClickWalletStore().putRecord(record);
  } catch (error) {
    console.error('Failed to put OneClickWalletRecord', error);
  }
}

export async function getOneClickWalletRecord(
  owner: string,
): Promise<OneClickWalletRecord | undefined> {
  try {
    return await getOneClickWalletStore().getRecord(owner);
  } catch (error) {
    console.error('Failed to get OneClickWalletRecord', error);
  }
  
return undefined;
}

export async function deleteOneClickWalletRecord(
  owner: string,
): Promise<void> {
  try {
    const ownerKey = owner.toLowerCase();
    await getOneClickWalletStore().deleteRecord(ownerKey);
  } catch (error) {
    console.error('Failed to delete OneClickWalletRecord', error);
  }
}

/**
 * Load and decrypt the 1CT wallet private key for a given owner (lowercased address).
 * This function only decrypts and returns the pk, does not write to any global state.
 */
export async function loadOneClickWalletPrivateKey(
  owner: string,
): Promise<string | undefined> {
  const record = await getOneClickWalletRecord(owner);
  if (!record) return undefined;

  return decryptPrivateKeyFromRecord(record);
}

const ONE_CLICK_WALLET_SIGN_MESSAGE = `LeverUp 1CT Wallet

You are about to create a dedicated One-Click Trading (1CT) wallet using the LeverUp protocol.
The private key for this wallet will be generated and encrypted locally on your device and will not be uploaded to any server.

Before signing, please carefully verify the following:
1. The website domain in your browser is: app.leverup.xyz
2. The connected wallet address is the one you intend to use
3. You understand that this signature is only used to deterministically derive your 1CT wallet and will NOT execute any on-chain transfers or trades

If all of the above is correct, please sign to complete the creation of your 1CT wallet.`;

/**
 * Full flow used by the 1CT modal:
 * 1. Ask the user to sign the standard 1CT message
 * 2. Deterministically derive the 1CT wallet private key from the signature (so that we can get same 1CT wallet address from same sig)
 * 3. Encrypt and store the 1CT wallet record in IndexedDB
 */
export async function createAndStoreOneClickWalletForOwner(params: {
  ownerAddress: string;
  signMessageAsync: (args: { message: string }) => Promise<Hex>;
}): Promise<OneClickWalletRecord> {
  if (typeof window === 'undefined') {
    throw new Error('Window is not available');
  }

  const signature = await params.signMessageAsync({
    message: ONE_CLICK_WALLET_SIGN_MESSAGE,
  });

  const privateKey = keccak256(signature);
  const account = privateKeyToAccount(privateKey);

  const record = await createEncryptedOneClickWalletRecord({
    owner: params.ownerAddress.toLowerCase(),
    oneClickWalletAddress: account.address,
    privateKey,
  });

  await putOneClickWalletRecord(record);

  return record;
}
